import { withAuthRecovery } from '@/lib/authRecovery'
import {
  createEmptyTrainerChecklistCriterionPhotos,
  type TrainerChecklistPhoto,
} from '@/lib/trainerChecklistReviews'
import type { TrainerChecklistComments, TrainerChecklistScores } from '@/lib/trainerChecklist'
import { getSupabase } from '@/lib/supabase'
import type { TrainerReviewDetail } from '@/services/trainerReviewService'
import { getTrainerReviewPhotoUrl, resolveTrainerReviewPhotoUrls } from '@/services/trainerReviewPhotoUrls'

export type PartnerTeacherStatus = 'candidate' | 'training' | 'working' | 'dismissed'
export type PartnerTeacherReviewSource = 'management_company' | 'senior_teacher'
export type PartnerTeacherQualification = 'stretch_pilates' | 'universal' | null
export type PartnerTeacherAttentionTone = 'error' | 'warning' | 'neutral'
export type PartnerTeacherAttentionKind = 'quality'
export type PartnerTeacherTrainingStatus = 'in_progress' | 'completed' | 'closed'
export type PartnerTeacherAttestationResult = 'passed' | 'retake' | 'refused'

export interface PartnerTeacherScope {
  partnerNumber: number
  cityId: number
  cityName: string
  accessKind: 'owner' | 'manager' | 'management_company' | 'senior_teacher'
}

export interface PartnerTeacherAttentionItem {
  key: string
  kind: PartnerTeacherAttentionKind
  label: string
  tone: PartnerTeacherAttentionTone
  priority: number
  dueDate: string | null
}

export interface PartnerTeacherTrainingAttestationAttempt {
  attemptId: string
  stage: 1 | 2 | 3
  attemptNo: number
  attestationDate: string | null
  result: PartnerTeacherAttestationResult
  createdAt: string
}

export interface PartnerTeacherTraining {
  trainingCycleId: string
  status: PartnerTeacherTrainingStatus
  startDate: string | null
  deadlineDate: string | null
  firstAttestationDate: string | null
  firstAttestationResult: PartnerTeacherAttestationResult | null
  secondAttestationDate: string | null
  secondAttestationResult: PartnerTeacherAttestationResult | null
  thirdAttestationDate: string | null
  thirdAttestationResult: PartnerTeacherAttestationResult | null
  completedAt: string | null
  closedAt: string | null
  attestationAttempts: PartnerTeacherTrainingAttestationAttempt[]
}


export interface PartnerTeacherListItem {
  teacherId: string
  fullName: string
  qualification: PartnerTeacherQualification
  status: PartnerTeacherStatus
  currentTraining: PartnerTeacherTraining | null
  reviewsCount: number
  latestReviewKey: string | null
  latestReviewDate: string | null
  latestReviewTrainingAt: string | null
  latestReviewQualityPercent: number | null
  latestReviewDirectionName: string | null
  averageQualityPercent: number | null
  managementCompanyReviewsCount: number
  managementCompanyLatestReviewKey: string | null
  managementCompanyLatestReviewDate: string | null
  managementCompanyLatestReviewTrainingAt: string | null
  managementCompanyLatestReviewQualityPercent: number | null
  managementCompanyAverageQualityPercent: number | null
  seniorTeacherReviewsCount: number
  seniorTeacherLatestReviewKey: string | null
  seniorTeacherLatestReviewDate: string | null
  seniorTeacherLatestReviewTrainingAt: string | null
  seniorTeacherLatestReviewQualityPercent: number | null
  seniorTeacherAverageQualityPercent: number | null
  attentionItems: PartnerTeacherAttentionItem[]
  isSeniorTeacher: boolean
}

export interface PartnerTeacherDashboard {
  scope: PartnerTeacherScope
  summary: {
    total: number
    working: number
    training: number
    attention: number
    dismissed: number
  }
  items: PartnerTeacherListItem[]
}

export interface PartnerTeacherReviewSummary {
  reviewKey: string
  reviewDate: string
  trainingAt: string | null
  directionName: string | null
  qualityPercent: number | null
  conclusion: string | null
  generalComment: string | null
  availableAt: string
  reviewSource: PartnerTeacherReviewSource
  reviewerName: string
}

export interface PartnerTeacherDetail {
  scope: PartnerTeacherScope
  teacherId: string
  fullName: string
  qualification: PartnerTeacherQualification
  status: PartnerTeacherStatus
  training: PartnerTeacherTraining | null
  reviewsCount: number
  latestReviewDate: string | null
  latestReviewQualityPercent: number | null
  averageQualityPercent: number | null
  managementCompanyReviewsCount: number
  managementCompanyLatestReviewDate: string | null
  managementCompanyLatestReviewQualityPercent: number | null
  managementCompanyAverageQualityPercent: number | null
  seniorTeacherReviewsCount: number
  seniorTeacherLatestReviewDate: string | null
  seniorTeacherLatestReviewQualityPercent: number | null
  seniorTeacherAverageQualityPercent: number | null
  attentionItems: PartnerTeacherAttentionItem[]
  isSeniorTeacher: boolean
  contactPhone: string | null
  contactEmail: string | null
  telegramUsername: string | null
  cityNames: string[]
  reviews: PartnerTeacherReviewSummary[]
}

function formatError(error: unknown): string {
  if (!error) return 'Неизвестная ошибка'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const candidate = error as { message?: string; details?: string; hint?: string; code?: string }
    return [candidate.message, candidate.details, candidate.hint, candidate.code]
      .filter(Boolean)
      .join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function asRecord(value: unknown): Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function asRows(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter(item => item != null && typeof item === 'object') as Array<Record<string, unknown>>
    : []
}

function text(value: unknown): string {
  return value == null ? '' : String(value)
}

function nullableText(value: unknown): string | null {
  return value == null || String(value).trim() === '' ? null : String(value)
}

function nullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function numberValue(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function mapScope(value: unknown): PartnerTeacherScope {
  const row = asRecord(value)
  return {
    partnerNumber: numberValue(row.partner_number),
    cityId: numberValue(row.city_id),
    cityName: text(row.city_name),
    accessKind: row.access_kind === 'manager'
      ? 'manager'
      : row.access_kind === 'management_company'
      ? 'management_company'
      : row.access_kind === 'senior_teacher'
        ? 'senior_teacher'
        : 'owner',
  }
}

function mapAttention(value: unknown): PartnerTeacherAttentionItem[] {
  return asRows(value).map(row => ({
    key: text(row.key),
    kind: row.kind as PartnerTeacherAttentionKind,
    label: text(row.label),
    tone: (row.tone as PartnerTeacherAttentionTone) || 'neutral',
    priority: numberValue(row.priority),
    dueDate: nullableText(row.due_date ?? row.dueDate),
  }))
}

function mapTraining(value: unknown): PartnerTeacherTraining | null {
  if (value == null) return null
  const row = asRecord(value)
  if (!row.training_cycle_id) return null
  return {
    trainingCycleId: text(row.training_cycle_id),
    status: row.status as PartnerTeacherTrainingStatus,
    startDate: nullableText(row.start_date),
    deadlineDate: nullableText(row.deadline_date),
    firstAttestationDate: nullableText(row.first_attestation_date),
    firstAttestationResult: (row.first_attestation_result as PartnerTeacherAttestationResult | null) ?? null,
    secondAttestationDate: nullableText(row.second_attestation_date),
    secondAttestationResult: (row.second_attestation_result as PartnerTeacherAttestationResult | null) ?? null,
    thirdAttestationDate: nullableText(row.third_attestation_date),
    thirdAttestationResult: (row.third_attestation_result as PartnerTeacherAttestationResult | null) ?? null,
    completedAt: nullableText(row.completed_at),
    closedAt: nullableText(row.closed_at),
    attestationAttempts: asRows(row.attestation_attempts).map(attempt => ({
      attemptId: text(attempt.attempt_id),
      stage: Number(attempt.stage) as 1 | 2 | 3,
      attemptNo: numberValue(attempt.attempt_no),
      attestationDate: nullableText(attempt.attestation_date),
      result: attempt.result as PartnerTeacherAttestationResult,
      createdAt: text(attempt.created_at),
    })),
  }
}

function mapListItem(value: unknown): PartnerTeacherListItem {
  const row = asRecord(value)
  return {
    teacherId: text(row.teacher_id),
    fullName: text(row.full_name),
    qualification: (row.qualification as PartnerTeacherQualification) ?? null,
    status: row.status as PartnerTeacherStatus,
    currentTraining: mapTraining(row.current_training),
    reviewsCount: numberValue(row.reviews_count),
    latestReviewKey: nullableText(row.latest_review_key),
    latestReviewDate: nullableText(row.latest_review_date),
    latestReviewTrainingAt: nullableText(row.latest_review_training_at),
    latestReviewQualityPercent: nullableNumber(row.latest_review_quality_percent),
    latestReviewDirectionName: nullableText(row.latest_review_direction_name),
    averageQualityPercent: nullableNumber(row.average_quality_percent),
    managementCompanyReviewsCount: numberValue(row.management_company_reviews_count),
    managementCompanyLatestReviewKey: nullableText(row.management_company_latest_review_key),
    managementCompanyLatestReviewDate: nullableText(row.management_company_latest_review_date),
    managementCompanyLatestReviewTrainingAt: nullableText(row.management_company_latest_training_at),
    managementCompanyLatestReviewQualityPercent: nullableNumber(row.management_company_latest_quality_percent),
    managementCompanyAverageQualityPercent: nullableNumber(row.management_company_average_quality_percent),
    seniorTeacherReviewsCount: numberValue(row.senior_teacher_reviews_count),
    seniorTeacherLatestReviewKey: nullableText(row.senior_teacher_latest_review_key),
    seniorTeacherLatestReviewDate: nullableText(row.senior_teacher_latest_review_date),
    seniorTeacherLatestReviewTrainingAt: nullableText(row.senior_teacher_latest_training_at),
    seniorTeacherLatestReviewQualityPercent: nullableNumber(row.senior_teacher_latest_quality_percent),
    seniorTeacherAverageQualityPercent: nullableNumber(row.senior_teacher_average_quality_percent),
    attentionItems: mapAttention(row.attention_items),
    isSeniorTeacher: row.is_senior_teacher === true,
  }
}

export async function getPartnerCityTeachers(
  studioInternalId: number,
  search = '',
  includeDismissed = false
): Promise<PartnerTeacherDashboard> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_partner_city_teachers', {
      p_studio_internal_id: studioInternalId,
      p_search: search.trim() || null,
      p_include_dismissed: includeDismissed,
    })
  )
  if (error) throw new Error(formatError(error))

  const payload = asRecord(data)
  const summary = asRecord(payload.summary)
  const rawItems = asRows(payload.items)
  // The registry does not render lesson date/time. Resolving it for every
  // teacher made the page call the historical review-display RPC with the
  // whole city and hit the authenticated statement timeout. Partner detail
  // payloads already contain their server-gated training_at value.
  const items = rawItems.map(mapListItem)

  return {
    scope: mapScope(payload.scope),
    summary: {
      total: numberValue(summary.total),
      working: numberValue(summary.working),
      training: numberValue(summary.training),
      attention: numberValue(summary.attention),
      dismissed: numberValue(summary.dismissed),
    },
    items: items
      .sort((left, right) => left.fullName.localeCompare(right.fullName, 'ru', { sensitivity: 'base' })),
  }
}

export async function getPartnerCityTeacherDetail(
  studioInternalId: number,
  teacherId: string
): Promise<PartnerTeacherDetail | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_partner_city_teacher_detail_enriched', {
      p_studio_internal_id: studioInternalId,
      p_teacher_id: teacherId,
    })
  )
  if (error) throw new Error(formatError(error))
  if (!data) return null

  const row = asRecord(data)
  const reviewRows = asRows(row.reviews)
  const scope = mapScope(row.scope)
  let contacts: Record<string, unknown> = {}
  if (scope.accessKind === 'owner' || scope.accessKind === 'manager' || scope.accessKind === 'senior_teacher') {
    const { data: contactData, error: contactError } = await withAuthRecovery(() =>
      supabase.rpc('get_partner_teacher_contacts', {
        p_studio_internal_id: studioInternalId,
        p_teacher_id: teacherId,
      })
    )
    if (!contactError && contactData) contacts = asRecord(contactData)
  }

  return {
    scope,
    teacherId: text(row.teacher_id),
    fullName: text(row.full_name),
    qualification: (row.qualification as PartnerTeacherQualification) ?? null,
    status: row.status as PartnerTeacherStatus,
    training: mapTraining(row.training),
    reviewsCount: numberValue(row.reviews_count),
    latestReviewDate: nullableText(row.latest_review_date),
    latestReviewQualityPercent: nullableNumber(row.latest_review_quality_percent),
    averageQualityPercent: nullableNumber(row.average_quality_percent),
    managementCompanyReviewsCount: numberValue(row.management_company_reviews_count),
    managementCompanyLatestReviewDate: nullableText(row.management_company_latest_review_date),
    managementCompanyLatestReviewQualityPercent: nullableNumber(row.management_company_latest_review_quality_percent),
    managementCompanyAverageQualityPercent: nullableNumber(row.management_company_average_quality_percent),
    seniorTeacherReviewsCount: numberValue(row.senior_teacher_reviews_count),
    seniorTeacherLatestReviewDate: nullableText(row.senior_teacher_latest_review_date),
    seniorTeacherLatestReviewQualityPercent: nullableNumber(row.senior_teacher_latest_review_quality_percent),
    seniorTeacherAverageQualityPercent: nullableNumber(row.senior_teacher_average_quality_percent),
    attentionItems: mapAttention(row.attention_items),
    isSeniorTeacher: row.is_senior_teacher === true,
    contactPhone: nullableText(contacts.phone_number),
    contactEmail: nullableText(contacts.email),
    telegramUsername: nullableText(contacts.telegram_username),
    cityNames: Array.isArray(contacts.city_names)
      ? contacts.city_names.map(value => String(value)).filter(Boolean)
      : [],
    reviews: reviewRows.map(review => ({
      reviewKey: text(review.review_key),
      reviewDate: text(review.review_date),
      trainingAt: nullableText(review.training_at),
      directionName: nullableText(review.direction_name),
      qualityPercent: nullableNumber(review.quality_percent),
      conclusion: nullableText(review.conclusion),
      generalComment: nullableText(review.general_comment),
      availableAt: text(review.available_at),
      reviewSource: review.review_source === 'senior_teacher' ? 'senior_teacher' : 'management_company',
      reviewerName: text(review.reviewer_name) || '—',
    })),
  }
}

function mapPhotoRowToPhoto(
  photoRow: Record<string, unknown>,
  resolvedPhotoUrls: Map<string, string>,
): TrainerChecklistPhoto {
  const storageBucket = text(photoRow.storage_bucket) || 'trainer-review-photos'
  const storagePath = text(photoRow.storage_path)
  const previewUrl = storagePath
    ? getTrainerReviewPhotoUrl(photoRow, resolvedPhotoUrls)
    : text(photoRow.external_url)

  const photoId = photoRow.photo_id == null ? undefined : Number(photoRow.photo_id)
  return {
    id: `photo-${photoId ?? crypto.randomUUID()}`,
    photoId,
    reviewScoreId: photoRow.review_score_id == null ? undefined : Number(photoRow.review_score_id),
    name: text(photoRow.original_name) || 'Фото',
    previewUrl,
    storageBucket,
    storagePath,
    mimeType: nullableText(photoRow.mime_type) ?? undefined,
    sizeBytes: photoRow.size_bytes == null ? undefined : Number(photoRow.size_bytes),
    widthPx: photoRow.width_px == null ? undefined : Number(photoRow.width_px),
    heightPx: photoRow.height_px == null ? undefined : Number(photoRow.height_px),
  } satisfies TrainerChecklistPhoto
}

export async function getPartnerTrainerReviewDetail(
  studioInternalId: number,
  reviewKey: string,
  options: { loadPhotoUrls?: boolean } = {},
): Promise<TrainerReviewDetail | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_partner_trainer_review_detail', {
      p_studio_internal_id: studioInternalId,
      p_review_key: reviewKey,
    })
  )
  if (error) throw new Error(formatError(error))

  const row = asRows(data)[0]
  if (!row) return null

  const scores = Object.create(null) as TrainerChecklistScores
  const comments = Object.create(null) as TrainerChecklistComments
  const criterionPhotos = createEmptyTrainerChecklistCriterionPhotos()
  const scoreReviewIds: Record<string, string> = {}
  const scoreRows = asRows(row.scores)
  const generalPhotoRows = asRows(row.general_photos).length > 0
    ? asRows(row.general_photos)
    : asRows(row.photos)
  const photoRows = [
    ...generalPhotoRows,
    ...scoreRows.flatMap(scoreRow => asRows(scoreRow.photos)),
  ]
  const resolvedPhotoUrls = options.loadPhotoUrls === false
    ? new Map<string, string>()
    : await resolveTrainerReviewPhotoUrls(photoRows)

  for (const scoreRow of scoreRows) {
    const criterionCode = text(scoreRow.criterion_code)
    if (!criterionCode) continue
    scores[criterionCode] = scoreRow.score == null ? null : Number(scoreRow.score)
    comments[criterionCode] = text(scoreRow.comment)
    if (scoreRow.review_score_id != null) {
      scoreReviewIds[criterionCode] = String(scoreRow.review_score_id)
    }
    criterionPhotos[criterionCode] = asRows(scoreRow.photos)
      .map(photoRow => mapPhotoRowToPhoto(photoRow, resolvedPhotoUrls))
  }

  const generalPhotos = generalPhotoRows.map(photoRow => mapPhotoRowToPhoto(photoRow, resolvedPhotoUrls))
  return {
    reviewId: reviewKey,
    teacherId: row.teacher_id == null ? null : String(row.teacher_id),
    meta: {
      studioId: row.studio_internal_id == null ? '' : String(row.studio_internal_id),
      city: text(row.city_name),
      studioAddress: text(row.studio_address),
      reviewDate: text(row.review_date),
      trainingDateTime: text(row.training_at),
      directionId: row.direction_id == null ? '' : String(row.direction_id),
      directionName: text(row.direction_name),
      coachName: text(row.coach_name),
    },
    scores,
    comments,
    generalComment: text(row.general_comment),
    generalPhotos,
    criterionPhotos,
    scoreReviewIds,
    totalScore: Number(row.total_score ?? 0),
    maxScore: Number(row.max_score ?? 0),
    qualityPercent: Number(row.quality_percent ?? 0),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
  }
}
